import React, { useEffect, useMemo, useRef, useState } from 'react';

interface AddZonePillProps {
  areaId: string;
  floorId: string;
  availableZoneIds: string[];
  saving: boolean;
  onAdd: (areaId: string, zoneId: string, floorId: string) => void;
  chipStyle: React.CSSProperties;
}

const compareZoneIds = (a: string, b: string) => {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return a.localeCompare(b);
};

const AddZonePill: React.FC<AddZonePillProps> = ({ areaId, floorId, availableZoneIds, saving, onAdd, chipStyle }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const sortedZones = useMemo(
    () => [...availableZoneIds].sort(compareZoneIds),
    [availableZoneIds],
  );

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const pillStyle: React.CSSProperties = {
    ...chipStyle,
    position: 'relative',
    borderStyle: 'dashed',
    color: sortedZones.length === 0 ? 'var(--muted, #a0aec0)' : 'var(--primary, #3182ce)',
    cursor: sortedZones.length === 0 || saving ? 'not-allowed' : 'pointer',
    opacity: sortedZones.length === 0 ? 0.6 : 1,
  };

  const handleOpen = () => {
    if (saving || sortedZones.length === 0) return;
    setOpen(true);
  };

  const handleSelect = (zoneId: string) => {
    if (!zoneId) return;
    onAdd(areaId, zoneId, floorId);
    setOpen(false);
  };

  return (
    <span ref={containerRef} style={pillStyle}>
      {!open ? (
        <button
          type="button"
          onClick={handleOpen}
          disabled={saving || sortedZones.length === 0}
          title={sortedZones.length === 0 ? 'No unassigned BACnet zones' : 'Add BACnet zone'}
          aria-label="Add BACnet zone"
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'inherit',
            padding: 0,
            lineHeight: 1,
            fontWeight: 600,
            color: 'inherit',
          }}
        >
          +
        </button>
      ) : (
        <select
          autoFocus
          className="ah-input"
          defaultValue=""
          disabled={saving}
          onChange={(event) => handleSelect(event.target.value)}
          onBlur={() => setOpen(false)}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            fontSize: 13,
            minWidth: 96,
            cursor: 'pointer',
          }}
          aria-label="Select BACnet zone to add"
        >
          <option value="">Add zone…</option>
          {sortedZones.map((zoneId) => (
            <option key={zoneId} value={zoneId}>
              Zone {zoneId}
            </option>
          ))}
        </select>
      )}
    </span>
  );
};

export default AddZonePill;
